// @flow
import React, { useLayoutEffect, useRef, useState } from 'react';
import styles from './StrategyPercentageCallouts.module.scss';

export const arrangePercentageLabels = (items, trackWidth) => {
  if (!(trackWidth > 0)) return [];
  return items.map((item) => ({
    ...item,
    left: item.center - item.segmentWidth / 2,
  }));
};

type Segment = { id: string, width: number, className: string, label: string };
type Props = { segments: Array<Segment>, Graph: any, className: string };

const StrategyPercentageBar = ({ segments, Graph, className }: Props) => {
  const trackRef = useRef(null);
  const labelRefs = useRef({});
  const labelsRef = useRef(null);
  const [layout, setLayout] = useState([]);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const positiveSegments = segments.filter((segment) => segment.width > 0);
  const activeSegment = positiveSegments.find((segment) => segment.id === activeId)
    || positiveSegments[0];

  const selectHoveredSegment = (event) => {
    const track = trackRef.current;
    if (!track || !positiveSegments.length || event.target.closest('[role="tooltip"]')) return;
    // Read the real rendered edges, including borders and fractional-pixel segments.
    const index = positiveSegments.findIndex((segment, segmentIndex) => (
      event.clientX < track.children[segmentIndex].getBoundingClientRect().right
    ));
    const segment = positiveSegments[index < 0 ? positiveSegments.length - 1 : index];
    setActiveId(segment.id);
    if (segment.id !== activeId) setDismissed(false);
  };

  useLayoutEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;
    const measure = () => {
      const trackRect = track.getBoundingClientRect();
      const trackWidth = track.clientWidth || trackRect.width;
      const trackHeight = track.clientHeight || trackRect.height;
      if (!(trackWidth > 0 && trackHeight > 0)) return;
      const labelsRect = labelsRef.current?.getBoundingClientRect() || trackRect;
      const items = [];
      let offset = 0;
      positiveSegments.forEach((segment, index) => {
        const label = labelRefs.current[segment.id];
        if (!label) return;
        const segmentRect = track.children[index]?.getBoundingClientRect();
        const segmentWidth = segmentRect?.width || (trackWidth * segment.width) / 100;
        const center = segmentRect?.width > 0
          ? segmentRect.left - labelsRect.left + segmentRect.width / 2
          : (trackWidth * (offset + segment.width / 2)) / 100;
        const top = segmentRect?.height > 0 ? segmentRect.top - labelsRect.top : 0;
        const height = segmentRect?.height || trackHeight;
        offset += segment.width;
        items.push({
          id: segment.id, center, segmentWidth, top, height,
        });
      });
      // Normal-size text stays centered; each original segment clips its own overflow.
      const next = arrangePercentageLabels(items, trackWidth);
      setLayout((previous) => (
        JSON.stringify(previous) === JSON.stringify(next) ? previous : next
      ));
    };
    measure();
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    if (observer) {
      observer.observe(track);
      Array.from(track.children).forEach((segment) => observer.observe(segment));
      positiveSegments.forEach((segment) => {
        const label = labelRefs.current[segment.id];
        if (label) observer.observe(label);
      });
    }
    window.addEventListener('resize', measure);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [segments]);

  return (
    <div
      className={styles.percentageTrack}
      role="button"
      aria-expanded={(hovered || focused) && !dismissed && positiveSegments.length > 0}
      tabIndex={positiveSegments.length ? 0 : undefined}
      aria-label={positiveSegments.map((segment) => segment.label).join('; ')}
      onMouseEnter={(event) => {
        selectHoveredSegment(event);
        setHovered(true);
        setDismissed(false);
      }}
      onMouseMove={selectHoveredSegment}
      onMouseLeave={() => setHovered(false)}
      onClick={(event) => {
        selectHoveredSegment(event);
        setFocused(true);
        setDismissed(false);
      }}
      onFocus={() => {
        setActiveId(positiveSegments[0]?.id || null);
        setFocused(true);
        setDismissed(false);
      }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') setDismissed(true);
        if ((event.key === 'ArrowLeft' || event.key === 'ArrowRight') && positiveSegments.length) {
          event.preventDefault();
          const index = positiveSegments.indexOf(activeSegment);
          const step = event.key === 'ArrowRight' ? 1 : -1;
          const next = (index + step + positiveSegments.length) % positiveSegments.length;
          setActiveId(positiveSegments[next].id);
          setFocused(true);
          setDismissed(false);
        }
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          setFocused(true);
          setDismissed(false);
        }
      }}
    >
      <div
        ref={trackRef}
        className={`${className} ${styles.trackSurface}`}
      >
        {positiveSegments.map((segment) => (
          <Graph
            key={segment.id}
            width={segment.width}
            className={segment.className}
            label={segment.label}
            showPercentage={false}
            showTooltip={false}
          />
        ))}
        <div
          ref={labelsRef}
          className={styles.labels}
          aria-hidden="true"
        >
          {positiveSegments.map((segment) => {
            const position = layout.find((item) => item.id === segment.id);
            return (
              <div
                key={segment.id}
                className={styles.labelSlot}
                style={{
                  left: position?.left || 0,
                  top: position?.top || 0,
                  width: position?.segmentWidth || 0,
                  height: position?.height || 0,
                  opacity: position ? 1 : 0,
                }}
              >
                <span
                  ref={(node) => { labelRefs.current[segment.id] = node; }}
                  className={styles.percentageLabel}
                >
                  {`${segment.width}%`}
                </span>
              </div>
            );
          })}
        </div>
      </div>
      {(hovered || focused) && !dismissed && activeSegment && (
        <div className={styles.hoverDetails} role="tooltip" aria-hidden="true">
          <div className={styles.hoverDetail}>
            <span className={[styles.hoverDot, activeSegment.className].join(' ')} />
            <span>{activeSegment.label}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default StrategyPercentageBar;
